import { Trans, useLingui } from '@lingui/react/macro';
import {
  Checkbox,
  DatePickerWithYear,
  Input,
  Label,
} from '@repo/videa-components';
import { useGetDateFNSLocale } from '@/locales/helpers';

export interface DependentInfoSectionProps {
  firstName: string;
  onFirstNameChange: (value: string) => void;
  lastName: string;
  onLastNameChange: (value: string) => void;
  dob: Date | undefined;
  onDobChange: (date: Date, source: 'CALENDAR') => void;
  memberId: string;
  onMemberIdChange: (value: string) => void;
  sameAsSubscriber: boolean;
  onSameAsSubscriberChange: (checked: boolean) => void;
}

export const DependentInfoSection = ({
  firstName,
  onFirstNameChange,
  lastName,
  onLastNameChange,
  dob,
  onDobChange,
  memberId,
  onMemberIdChange,
  sameAsSubscriber,
  onSameAsSubscriberChange,
}: DependentInfoSectionProps) => {
  const { t } = useLingui();
  const dateLocale = useGetDateFNSLocale();

  return (
    <section className="vai-space-y-4">
      <h3 className="vai-text-sm vai-font-medium vai-text-gray-700">
        <Trans>Dependent Information</Trans>
      </h3>
      <div className="vai-grid vai-gap-4 sm:vai-grid-cols-3">
        <div className="vai-space-y-2">
          <Label htmlFor="dep-first-name">
            <Trans>First Name</Trans>
            {!firstName.trim() && <span className="vai-text-red-500 vai-ml-0.5">*</span>}
          </Label>
          <Input
            id="dep-first-name"
            value={firstName}
            onChange={(e) => onFirstNameChange(e.target.value)}
            placeholder={t`First Name`}
          />
        </div>
        <div className="vai-space-y-2">
          <Label htmlFor="dep-last-name">
            <Trans>Last Name</Trans>
            {!lastName.trim() && <span className="vai-text-red-500 vai-ml-0.5">*</span>}
          </Label>
          <Input
            id="dep-last-name"
            value={lastName}
            onChange={(e) => onLastNameChange(e.target.value)}
            placeholder={t`Last Name`}
          />
        </div>
        <div className="vai-space-y-2">
          <Label>
            <Trans>DOB</Trans>
            {!dob && <span className="vai-text-red-500 vai-ml-0.5">*</span>}
          </Label>
          <DatePickerWithYear
            date={dob}
            setDate={onDobChange}
            placeholderText={t`Pick a date`}
            locale={dateLocale}
            buttonClassName="!vai-justify-start"
          />
        </div>
      </div>
      <div className="vai-grid vai-gap-4 sm:vai-grid-cols-3">
        <div className="vai-space-y-2">
          <div className="vai-flex vai-items-center vai-gap-2">
            <Label htmlFor="dep-member-id">
              <Trans>Member ID</Trans>
              {!(sameAsSubscriber || memberId.trim()) && <span className="vai-text-red-500 vai-ml-0.5">*</span>}
            </Label>
            <div className="vai-flex vai-items-center vai-gap-1.5">
              <Checkbox
                id="dep-same-as-subscriber"
                checked={sameAsSubscriber}
                onCheckedChange={(checked) =>
                  onSameAsSubscriberChange(checked === true)
                }
              />
              <Label
                htmlFor="dep-same-as-subscriber"
                className="vai-text-xs vai-font-normal vai-text-gray-500"
              >
                <Trans>Same as subscriber</Trans>
              </Label>
            </div>
          </div>
          <Input
            id="dep-member-id"
            value={memberId}
            onChange={(e) => onMemberIdChange(e.target.value)}
            placeholder={t`Member ID`}
            disabled={sameAsSubscriber}
          />
        </div>
      </div>
    </section>
  );
};
